import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type React from 'react';

interface ProgressDialogProps {
  open: boolean;
  onClose: () => void;
  onCancel?: () => void;
  progress: number;
  status: 'loading' | 'success' | 'error' | 'canceled';
  statusMessage: string;
  title?: string;
  etaSeconds?: number | null;
}

const ProgressDialog: React.FC<ProgressDialogProps> = ({
  open,
  onClose,
  onCancel,
  progress,
  status,
  statusMessage,
  title = 'Processando',
  etaSeconds,
}) => {
  const formatEta = (seconds: number) => {
    const safe = Math.max(1, Math.floor(seconds));
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = safe % 60;

    if (h > 0) return `~${h}h ${m > 0 ? `${m}min` : ''}`.trim();
    if (safe >= 60) return `~${m} min`;
    return `~${s}s`;
  };

  const clampedProgress = Math.max(0, Math.min(100, progress));

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={status !== 'loading' ? onClose : undefined}>
      <DialogTitle component="div" sx={{ pb: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
            {status === 'canceled' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {statusMessage}
              </Typography>
            )}
          </Box>
          {status !== 'loading' && (
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1.5 }}>
        <Box>
          {status === 'loading' && (
            <Box>
              <Box sx={{ px: 1.5, py: 1, borderRadius: 1.5, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.1), border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.3)}`, mb: 1.25 }}>
                <Typography variant="body2" color="text.secondary">{statusMessage}</Typography>
                {typeof etaSeconds === 'number' && etaSeconds > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Resta {formatEta(etaSeconds)}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>Progresso</Typography>
                <Typography variant="h6" fontWeight={700}>{`${Math.round(clampedProgress)}%`}</Typography>
              </Box>

              <LinearProgress variant="determinate" value={clampedProgress} color="info" sx={{ width: '100%', borderRadius: 1, height: 8, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.15), '& .MuiLinearProgress-bar': { width: `${clampedProgress}% !important`, transform: 'translateX(0) !important', transition: 'width 280ms ease !important' } }} />
            </Box>
          )}

          {status === 'success' && (
            <Box sx={{ color: 'success.main', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body1">{statusMessage || 'Concluído com sucesso!'}</Typography>
            </Box>
          )}

          {status === 'canceled' && (
            <Box sx={{ color: 'warning.main', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5 }}>
              <BlockRoundedIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body1">Operação cancelada</Typography>
            </Box>
          )}

          {status === 'error' && (
            <Box sx={{ color: 'error.main', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5 }}>
              <ErrorOutlineIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body1">Erro</Typography>
              <Typography variant="body2" color="text.secondary">{statusMessage}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, gap: 1, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
        {status === 'loading' ? (
          <>
            <Button disabled color="secondary" variant="contained" sx={{ minWidth: 110 }}>Aguardando</Button>
            <Button onClick={onCancel} variant="contained" color="error" sx={{ minWidth: 110 }}>Cancelar</Button>
          </>
        ) : (
          <Button onClick={onClose} variant="contained" color={status === 'error' ? 'error' : status === 'canceled' ? 'warning' : 'primary'} sx={{ minWidth: 110 }}>Fechar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProgressDialog;
